import { useCallback, useEffect, useState } from "react";
import { Compass, LogOut, Trash2, ArrowRight, Loader2, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { capi, useCustomer } from "@/lib/customer";

const STATUS_CHIP = {
  not_started: { label: "Not explored", color: "#94A3B8" },
  in_progress: { label: "Exploring", color: "#60A5FA" },
  needs_review: { label: "Discuss", color: "#FB923C" },
  ready: { label: "Ready", color: "#34D399" },
};

export default function MyWorld({ onOpenJourney, onExplore, reloadKey }) {
  const { customer, logout } = useCustomer();
  const [journeys, setJourneys] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await capi.get("/customer/journeys");
      setJourneys(data);
    } catch {
      setJourneys([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, reloadKey]);

  const remove = async (jid, e) => {
    e.stopPropagation();
    try {
      await capi.delete(`/customer/journeys/${jid}`);
      setJourneys((js) => js.filter((j) => j.id !== jid));
      toast.success("Journey removed");
    } catch {
      toast.error("Could not remove journey");
    }
  };

  const firstName = (customer?.name || "there").split(" ")[0];

  return (
    <div className="mx-auto max-w-lg px-4 pb-24 pt-5" data-testid="my-world-view">
      <div className="flex items-start justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-amber-400/90">My World</p>
          <h1 className="mt-1 text-2xl font-semibold text-white" style={{ fontFamily: "'Syne', sans-serif" }} data-testid="myworld-greeting">
            Hello, {firstName}
          </h1>
          <p className="mt-1 text-xs text-slate-400">Your coordinated journeys, all in one place.</p>
        </div>
        <button data-testid="myworld-logout-btn" onClick={logout} className="flex items-center gap-1.5 rounded-full border border-white/12 px-3 py-1.5 text-[11px] font-semibold text-slate-300">
          <LogOut size={13} /> Sign out
        </button>
      </div>

      {loading ? (
        <div className="mt-16 flex justify-center"><Loader2 className="animate-spin text-amber-300" /></div>
      ) : journeys.length === 0 ? (
        <div className="mt-14 flex flex-col items-center text-center">
          <div className="grid h-16 w-16 place-items-center rounded-2xl border border-white/10 bg-white/5 text-amber-300"><Compass size={28} /></div>
          <h2 className="mt-5 text-lg font-semibold text-white">No journeys yet</h2>
          <p className="mt-2 max-w-xs text-sm text-slate-300">Explore the world, choose a destination and build your first coordinated journey.</p>
          <button data-testid="myworld-explore-btn" onClick={onExplore} className="mt-5 flex items-center gap-2 rounded-full bg-amber-300 px-6 py-2.5 text-sm font-semibold text-[#07090E]">
            <Globe2 size={16} /> Explore the World
          </button>
        </div>
      ) : (
        <div className="mt-6 space-y-3" data-testid="myworld-journey-list">
          {journeys.map((j) => (
            <button
              key={j.id}
              data-testid={`myworld-journey-${j.country_code}`}
              onClick={() => onOpenJourney(j)}
              className="group w-full overflow-hidden rounded-2xl border border-white/[0.08] p-4 text-left transition-all hover:border-amber-300/30"
              style={{ background: "linear-gradient(135deg, rgba(226,194,117,0.08), rgba(13,17,26,0.5))" }}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="text-3xl">{j.flag}</span>
                  <div>
                    <p className="text-sm font-semibold text-white">{j.country_name}</p>
                    <p className="text-[11px] text-slate-300 line-clamp-1">{j.objective}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid={`myworld-delete-${j.country_code}`}
                    onClick={(e) => remove(j.id, e)}
                    className="grid h-7 w-7 place-items-center rounded-lg border border-red-400/25 text-red-300/80"
                  >
                    <Trash2 size={13} />
                  </span>
                  <ArrowRight size={16} className="text-amber-300" />
                </div>
              </div>
              <div className="mt-3 flex items-center gap-3">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/10">
                  <div className="h-full rounded-full bg-emerald-400" style={{ width: `${j.progress ?? 0}%` }} />
                </div>
                <span className="font-mono text-xs font-semibold text-emerald-300">{j.progress ?? 0}%</span>
              </div>
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-emerald-300" style={{ background: "rgba(52,211,153,0.12)" }}>
                  {j.readiness?.strengths ?? 0} strength{(j.readiness?.strengths ?? 0) === 1 ? "" : "s"}
                </span>
                {(j.readiness?.flags?.length ?? 0) > 0 && (
                  <span className="rounded-full px-2 py-0.5 text-[10px] font-semibold text-amber-300" style={{ background: "rgba(251,146,60,0.14)" }}>
                    {j.readiness.flags.length} to discuss
                  </span>
                )}
                {(j.nodes || []).slice(0, 2).map((n) => {
                  const s = STATUS_CHIP[n.status] || STATUS_CHIP.not_started;
                  return (
                    <span key={n.id} className="rounded-full px-2 py-0.5 text-[9px] font-medium" style={{ color: s.color, background: `${s.color}1f` }}>
                      {n.title.split(" ").slice(0, 2).join(" ")}: {s.label}
                    </span>
                  );
                })}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
